import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar, MatSnackBarConfig, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { Guid } from 'guid-typescript';
import { ProjectBudget } from '../../../../../common/models/project';
import { ProjectService } from '../../../../../common/services/project.service';
import { ProjectBudgetType } from '../../../../../common/models/common';
import { ProjectBudgetStatus } from '../../../../../common/enums/project-budget-status';
import { forkJoin, Subject, takeUntil } from 'rxjs';
import { GenericDialogComponent } from '../../../../../common/components/dialog/dialog';


@Component({
  standalone: true,
  selector: 'app-project-budgets',
  templateUrl: './budgets.component.html',
  styleUrls: ['./budgets.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    MatSnackBarModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatDialogModule
  ]
})
export class ProjectBudgetsComponent implements OnInit {
  private destroy$ = new Subject<void>();
  
  public ProjectBudgetStatus = ProjectBudgetStatus;
  public ProjectBudgetType = ProjectBudgetType;

  projectId!: string;
  projectBudgets: ProjectBudget[] = [];
  filteredBudgets: ProjectBudget[] = [];
  isHandset: boolean = false;

  projectApprovedAmount: number = 0;

  isBudgetSynced(budget: ProjectBudget): boolean {
    return budget.isSynced(this.projectApprovedAmount);
  }

  canCreateReallocation(budget: ProjectBudget): boolean {
    return budget.canCreateReallocation(this.projectBudgets);
  }

  private readonly snackbarConfig: MatSnackBarConfig = {
    duration: 3000,
    horizontalPosition: 'right',
    verticalPosition: 'top'
  };

  constructor(
    private projectService: ProjectService,
    private route: ActivatedRoute,
    private snackBar: MatSnackBar,
    private router: Router,
    private breakpointObserver: BreakpointObserver,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.breakpointObserver.observe([Breakpoints.XSmall, Breakpoints.Small]).pipe(takeUntil(this.destroy$)).subscribe(result => {
        this.isHandset = result.matches;
      });

    this.projectId = this.route.snapshot.paramMap.get('projectId') || '';
    this.fetchBudgets(this.projectId);
  }

  ngOnDestroy() {
      this.destroy$.next();
      this.destroy$.complete();
  }

  private fetchBudgets(projectId: string): void {
     forkJoin({
      project: this.projectService.getProject(Guid.parse(projectId)),
      projectBudgets: this.projectService.getProjectBudgets(Guid.parse(projectId))
     }).pipe(takeUntil(this.destroy$)).subscribe({
      next: ({ project, projectBudgets }) => {
        this.projectApprovedAmount = project.approvedAmount;
        if (projectBudgets) {
          this.projectBudgets = projectBudgets.map(b => ProjectBudget.fromJson(b));
          this.filteredBudgets = [...this.projectBudgets];
        }
      },
      error: (err) => {
        console.error('Error loading project budgets', err);
        this.snackBar.open('Došlo je do greške prilikom učitavanja projektnog proračuna.', 'Zatvori', this.snackbarConfig);
      }
    });
  }

  applySearch(searchTerm: string): void {
    searchTerm = searchTerm.trim().toLowerCase();
    if (searchTerm) {
        this.filteredBudgets = this.projectBudgets.filter((budget: ProjectBudget) => {
        return (
            true
        );
        });
    } else {
        this.filteredBudgets = [...this.projectBudgets];
    }
  }

  confirmBudgetReallocation(budget: ProjectBudget): void {
    const dialogRef = this.dialog.open(GenericDialogComponent, {
      width: '50%',
      data: {
        title: 'Potvrdite novu prenamjenu sredstava',
        message: `Jeste li sigurni da želite kreirati prenamjenu sredstava iz proračuna, verzija ${budget.budgetVersion}?`,
        confirmButtonText: 'Da',
        cancelButtonText: 'Ne',
      },
    });

    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result) => {
      if (result === true) {
        this.createBudgetReallocation(budget);
      }
    });
  }

  private createBudgetReallocation(budget: ProjectBudget) {
    this.projectService.createProjectBudgetReallocation(Guid.parse(this.projectId), { sourceProjectBudgetId: budget.id }).pipe(takeUntil(this.destroy$)).subscribe({
        next: () => {
          this.fetchBudgets(this.projectId);
          this.snackBar.open('Prenamjena sredstava je uspješno kreirana.', 'Zatvori', this.snackbarConfig);          
        },
        error: (err) => {
          console.error('Error creating budget reallocation', err);
          this.snackBar.open('Došlo je do greške pri kreiranja prenamjene sredstava.', 'Zatvori', this.snackbarConfig);
        }
      })
  }

  confirmSubmit(budget: ProjectBudget): void {
    const dialogRef = this.dialog.open(GenericDialogComponent, {
      width: '50%',
      data: {
        title: 'Potvrdite predaju',
        message: `Jeste li sigurni da želite predati proračun verzija broj ${budget.budgetVersion}?`,
        confirmButtonText: 'Da',
        cancelButtonText: 'Ne',
      },
    });

    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result) => {
      if (result === true) {
        this.submitBudget(budget);
      }
    });
  }

  private submitBudget(budget: ProjectBudget): void {
      this.projectService.changeProjectBudgetStatus(Guid.parse(this.projectId), Guid.parse(budget.id), { targetStatus: ProjectBudgetStatus.Submitted }).pipe(takeUntil(this.destroy$)).subscribe({
        next: () => {
          this.fetchBudgets(this.projectId);
          this.snackBar.open('Proračun je uspješno predložen.', 'Zatvori', this.snackbarConfig);
        },
        error: (err) => {
          console.error('Error submitting budget', err);
          this.snackBar.open('Došlo je do greške pri predlaganju proračuna.', 'Zatvori', this.snackbarConfig);
        }
      });
  }

  confirmResubmit(budget: ProjectBudget): void {
    const dialogRef = this.dialog.open(GenericDialogComponent, {
      width: '50%',
      data: {
        title: 'Potvrdite ponovnu predaju',
        message: `Jeste li sigurni da želite ponovno predati proračun verzija broj ${budget.budgetVersion}?`,
        confirmButtonText: 'Da',
        cancelButtonText: 'Ne',
      },
    });

    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result) => {
      if (result === true) {
        this.resubmitBudget(budget);
      }
    });
  }

  private resubmitBudget(budget: ProjectBudget): void {
    this.projectService.changeProjectBudgetStatus(Guid.parse(this.projectId), Guid.parse(budget.id), { targetStatus: ProjectBudgetStatus.Resubmitted }).pipe(takeUntil(this.destroy$)).subscribe({
        next: () => {
          this.fetchBudgets(this.projectId);
          this.snackBar.open('Proračun je uspješno predložen.', 'Zatvori', this.snackbarConfig);
        },
        error: (err) => {
          console.error('Error resubmitting budget', err);
          this.snackBar.open('Došlo je do greške pri predlaganju proračuna.', 'Zatvori', this.snackbarConfig);
        }
      });
  }

  downloadBudget(projectBudget: ProjectBudget): void {
      this.projectService.downloadProjectBudget(this.projectId, projectBudget.id).pipe(takeUntil(this.destroy$)).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'B2 Obrazac proračuna programa ili projekta.xls';
        a.click();
        window.URL.revokeObjectURL(url);
      },
      error: (err) => {
        console.error('Error downloading project budget', err);
      }
    });
  }
  
  goToViewBudget(projectBudget: ProjectBudget): void {
    this.router.navigate(['association', 'projects', this.projectId, 'budgets', projectBudget.id, 'view']);
 }

  goToEditBudget(projectBudget: ProjectBudget): void {
    this.router.navigate(['association', 'projects', this.projectId, 'budgets', projectBudget.id, 'edit']);
  }

}
